<template>
  <div>
    <tool-bar :title="toolbarTitle"></tool-bar>
    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false">
    </error-alert-item>

    <activity-redirect-item
      :loan-tasks="tasks"
      :current-loan-task="currentTaskName"
      :is-loan-task="true"
      :redirect-activity="redirectActivity"
    ></activity-redirect-item>

    <stage-comment-item v-if="!showRefreshItem"></stage-comment-item>

    <refresh-item
      :show="showRefreshItem"
      @onRefresh="getStageConfigs"
    ></refresh-item>

    <div v-if="lenderPlatformMessage"
         class="margin_top_20 margin-left-10 margin-right-10">
      <weex-text
        class="font-size-sm-base text-color-black"
        v-html="lenderPlatformMessage"
      ></weex-text>
    </div>

    <div v-else-if="showUI" class="margin_top_20 margin-10">
      <div v-if="nachOptions.length">
        <div class="item-heading-desc-div">
          <weex-text class="item-heading">{{getEmiPaymentInstructionsHeader}}</weex-text>
        </div>
        <weex-text class="item-bold-heading" v-if="nachOptions && nachOptions.length>1">{{getEmiOptionsHeader}}</weex-text>
        <div class="margin-top-10">
          <div v-for="nachOption in nachOptions"
               class="margin_top_20"
               @click="onNachOptionSelected(nachOption)"
               :key="nachOption.nachMode">
            <div class="item-row-alignment-center">
              <weex-image
                class="ezcred-radio-image"
                :src="radioButtonLink(nachOption)"
              ></weex-image>
              <weex-text class="ezcred-radio-text-font-16">{{nachOption.title}}
              </weex-text>
            </div>
            <weex-text class="text-color-gray font-size-sm-base margin-left-30">
              {{nachOption.description}}
            </weex-text>
          </div>
        </div>
        <wxc-button
          text="Submit"
          class="button_horizontal_center margin_top_20"
          type="blue"
          @wxcButtonClicked="onSubmit">
        </wxc-button>
      </div>
      <div v-else>
        <weex-text class="item-heading">{{noOptionPresentMessage}}</weex-text>
      </div>
    </div>
  </div>
</template>

<script>
import ToolBar from "../../layout/ToolBar";
import Loading from "../../layout/Loading";
import ErrorAlertItem from "../../layout/ErrorAlertItem";
import ActivityRedirectItem from "../../layout/ActivityRedirectItem";
import StageCommentItem from "../../layout/StageCommentItem";
import WxcButton from "../../../../weex-ui/packages/wxc-button/index";
import DataSource from "../../../data/DataSource";
import LoanScopeDataHolder from "../../../data/model/LoanScopeDataHolder";
import AlertService from "../../core/alert/AlertService";
import ActivityNavigator, { Activities } from "../../../utils/ActivityNavigator";
import { ImageResourcesUtil } from "../../../utils/ImageResourcesUtil";
import AttributeUtil from "../../../utils/AttributeUtil";
import { EntityType } from "../../../utils/EntityType";
import RefreshItem from "../../layout/RefreshItem";
import { MessageConstants } from "../../../utils/MessageConstantUtil";
import storageUtil from "../../../utils/StorageUtil";

const E_MANDATE = "E_MANDATE";
const PHYSICAL_MANDATE = "PHYSICAL_MANDATE";
const PHOTO_MANDATE = "PHOTO_MANDATE";
const MANDATE_MODE_SELECTED_KEY = "mandate.mode.selected";
const MANDATE_MODE_SELECTED_NAME = "Mandate Mode Selected";

export default {
  name: "NachOptionsActivity",
  components: {
    ActivityRedirectItem,
    ErrorAlertItem,
    Loading,
    ToolBar,
    WxcButton,
    StageCommentItem,
    RefreshItem
  },
  data: () => ({
    toolbarTitle: "EMI Payment Instructions",
    loading: false,
    showUI: false,
    networkError: {},
    nachOptions: [],
    selectedNachMode: null,
    errorAlertEvent: false,
    showRefreshItem: false,
    lenderPlatformMessage: ""
  }),
  methods: {
    showToast(message) {
      AlertService.showToast(message);
    },

    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    radioButtonLink(e) {
      return e.checked ? ImageResourcesUtil.getImageLink(ImageResourcesUtil.CHECKED_RADIO)
        : ImageResourcesUtil.getImageLink(ImageResourcesUtil.UNCHECKED_RADIO);
    },

    onNachOptionSelected(e) {
      this.nachOptions.forEach(nachOption => {
        nachOption.checked = false;
      });
      e.checked = true;
      this.selectedNachMode = e.nachMode;
    },

    onSubmit() {
      if (this.selectedNachMode) {
        this.loading = true;
        const attributeDto = AttributeUtil.getAttributeUpdateDto(
          EntityType.LOANS,
          LoanScopeDataHolder.getLoanId(),
          AttributeUtil.getSingleAttributeData(
            MANDATE_MODE_SELECTED_KEY,
            MANDATE_MODE_SELECTED_NAME,
            this.selectedNachMode
          )
        );
        DataSource.createOrUpdateAttribute(attributeDto).then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data);
          } else {
            let redirectActivity = null;
            switch (this.selectedNachMode) {
              case E_MANDATE:
                redirectActivity = Activities.ENACH;
                break;
              case PHYSICAL_MANDATE:
                redirectActivity = Activities.NACH_FORM;
                break;
              case PHOTO_MANDATE:
                redirectActivity = Activities.NACH_FORM;
                break;
            }
            ActivityNavigator.navigateTo(redirectActivity);
          }
        });
      } else {
        this.showToast(MessageConstants.SELECT_OPTION_TO_CONTINUE);
      }
    },

    getStageConfigs() {
      this.showRefreshItem = false;
      this.loading = true;
      DataSource.getLoanStageConfigData(LoanScopeDataHolder.getLoanId(), this.currentTaskName)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data);
            this.showRefreshItem = true;
          } else {
            const stageProperties = res.data && res.data.properties ? res.data.properties : {};
            if (stageProperties.completeOnLenderPlatform) {
              const lenderPlatformDetails = stageProperties.lenderPlatformDetails;
              if (lenderPlatformDetails && lenderPlatformDetails.message) {
                this.lenderPlatformMessage = lenderPlatformDetails.message;
              }
            } else {
              this.showUI = true;
              const nachStageInstructions = stageProperties['stage.instructions'].nachStageInstructions;
              if (nachStageInstructions) {
                for (const [nachMode, nachInstructions] of Object.entries(nachStageInstructions)) {
                  const nachOption = {
                    nachMode: nachMode,
                    checked: false,
                    title: nachInstructions.optionMessage,
                    description: nachInstructions.instructions
                  };
                  this.nachOptions.push(nachOption);
                }
                if(this.nachOptions && this.nachOptions.length == 1){
                  this.onNachOptionSelected(this.nachOptions[0])
                }
              }
            }
          }
        })
    }
  },

  created: function () {
    this.getStageConfigs();
  },

  computed: {
    redirectActivity() {
      return Activities.LOAN_DETAILS;
    },

    getEmiOptionsHeader() {
      return "Please choose either of the options";
    },

    tasks() {
      return [Activities.NACH];
    },

    currentTaskName() {
      return LoanScopeDataHolder.getCurrentLoanTask()
        ? LoanScopeDataHolder.getCurrentLoanTask().name
        : "";
    },

    noOptionPresentMessage() {
      return "Nach/Mandate registration is not allowed for provided Bank Account Details. Loan cannot be processed further. Please contact support.";
    },

    getEmiPaymentInstructionsHeader() {
      return "To pay your EMIs on time, you can set up automated payment instructions or register a mandate against your bank. Please follow the steps to register your payment instructions";
    },
  }
}
</script>

<style scoped src="../../../assets/style/base.css"></style>

